import { useState, useEffect, useLayoutEffect, useCallback, useRef } from "react";
import { loadDrafts, loadDraftMeta, saveDraftsLocally, canSyncDrafts, markDraftSynced, loadDraftOwner } from "./draftStore";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EMPTY_PROGRESS, normalizeCompassProgress, reconfirmAfterWorksheetEdit } from "./progressRules";

export const COMPASS_PROGRESS_ID = "compass-project-progress-v1";

function progressFor(user) {
  const owner = loadDraftOwner();
  const canRead = user?.id ? owner === null || owner === user.id : owner === null;
  return normalizeCompassProgress(canRead ? loadDrafts()[COMPASS_PROGRESS_ID] : EMPTY_PROGRESS);
}

function nextTimestamp(meta) {
  const now = Date.now();
  const previous = new Date(meta[COMPASS_PROGRESS_ID] || 0).getTime();
  return new Date(Math.max(now, Number.isFinite(previous) ? previous + 1 : now)).toISOString();
}

// Returns progress, a synchronous setter, and human-readable save feedback.
export function useCompassProgress() {
  const { user } = useAuth();
  const identity = user?.id || "anonymous";
  const [progress, setProgressState] = useState(() => progressFor(user));
  const [saveStatus, setSaveStatus] = useState("");
  const progressRef = useRef(progress);
  const identityRef = useRef(identity);
  const userRef = useRef(user);
  const saveQueueRef = useRef(Promise.resolve());
  userRef.current = user;

  // Reset and (only when ownership permits it) hydrate immediately on account
  // changes, rather than showing the previous founder's data for a frame.
  useLayoutEffect(() => {
    if (identityRef.current === identity) return;
    identityRef.current = identity;
    const hydrated = progressFor(user);
    progressRef.current = hydrated;
    setProgressState(hydrated);
    setSaveStatus("");
  }, [identity, user]);

  // Listen for draft merge completions
  useEffect(() => {
    const onMerged = () => {
      const owner = loadDraftOwner();
      const ownerMatches = user?.id ? owner === user.id : owner === null;
      if (ownerMatches) {
        const drafts = loadDrafts();
        const mergedProgress = normalizeCompassProgress(drafts[COMPASS_PROGRESS_ID]);
        progressRef.current = mergedProgress;
        setProgressState(mergedProgress);
        setSaveStatus(canSyncDrafts(user) ? "Synced to your account." : "Saved in this browser.");
      }
    };
    window.addEventListener("compass-drafts-merged", onMerged);
    return () => window.removeEventListener("compass-drafts-merged", onMerged);
  }, [user]);

  const setProgress = useCallback((updater) => {
    const updated = normalizeCompassProgress(
      typeof updater === "function" ? updater(progressRef.current) : updater
    );
    progressRef.current = updated;
    setProgressState(updated);

    const owner = loadDraftOwner();
    const allowed = user?.id ? owner === null || owner === user.id : owner === null;
    const meta = allowed ? loadDraftMeta() : {};
    const now = nextTimestamp(meta);
    const localResult = allowed
      ? saveDraftsLocally(
          { ...loadDrafts(), [COMPASS_PROGRESS_ID]: updated },
          { ...meta, [COMPASS_PROGRESS_ID]: now },
          canSyncDrafts(user) ? user.id : undefined
        )
      : { ok: false, reason: "owner-mismatch" };

    if (!localResult.ok) {
      setSaveStatus(localResult.reason === "owner-mismatch"
        ? "Could not save on this device until your account drafts finish loading."
        : "Could not save in this browser because local storage is unavailable.");
    } else if (!canSyncDrafts(user)) {
      setSaveStatus("Saved in this browser.");
    } else {
      const savingUserId = user.id;
      setSaveStatus("Saving to your account…");
      // Keep PUTs ordered so an older response can never win over a later edit.
      saveQueueRef.current = saveQueueRef.current
        .catch(() => {})
        .then(() => {
          if (userRef.current?.id !== savingUserId) return null;
          return api.put(
            `/compass/drafts/${COMPASS_PROGRESS_ID}`,
            { values: updated, updated_at: now },
            { skipNetToast: true }
          );
        })
        .then((response) => {
          if (!response || userRef.current?.id !== savingUserId) return;
          markDraftSynced(COMPASS_PROGRESS_ID, now);
          setSaveStatus(localResult.ok
            ? "Synced to your account."
            : "Synced to your account, but local storage is unavailable.");
        })
        .catch((error) => {
          if (userRef.current?.id === savingUserId) {
            const validationMessage = error?.response?.data?.detail;
            setSaveStatus(validationMessage
              ? `Saved in this browser, but account sync was rejected: ${validationMessage}`
              : localResult.ok
                ? "Saved offline — sync pending."
                : "Could not save locally; account sync is still pending.");
          }
        });
    }
  }, [user]);

  useEffect(() => {
    const onWorksheetSaved = (event) => {
      const { templateId, before, after } = event.detail;
      const current = progressRef.current;
      const next = reconfirmAfterWorksheetEdit(current, templateId, before, after);
      if (next !== current) setProgress(next);
    };
    window.addEventListener("compass-worksheet-saved", onWorksheetSaved);
    return () => window.removeEventListener("compass-worksheet-saved", onWorksheetSaved);
  }, [setProgress]);

  return [progress, setProgress, saveStatus];
}

